import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Box, Typography, Popover, Divider } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { getRestackTokens } from '../utils';
import { getChatRadii, getChatBorders } from '../chatStyles';
import { PublicIcon } from '../../../../shared/icons';

/**
 * EnhancedMarkdownRenderer - Robust markdown parser for AI-generated content
 *
 * Handles:
 * - Headers (# ## ###)
 * - Bold (**text**) and Italic (*text*)
 * - Bullet lists (-, *, •)
 * - Numbered lists (1., 2., etc)
 * - Inline code (`code`)
 * - Code blocks (```code```)
 * - Citations ([1], [2]) - now clickable with tooltips
 * - Flexible spacing (works with single or double line breaks)
 */

/**
 * CitationTooltip Component - Displays citation details in a popover
 * Memoized to prevent flickering on parent re-renders
 */
const CitationTooltipComponent = ({ citationNumber, citationData, theme, tokens, children }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const shouldAutoFocusRef = useRef(false);
  const linkRef = useRef(null);

  const popoverId = `citation-popover-${citationNumber}`;

  // Close handler - fires on click-away or escape
  const handleClose = useCallback(() => {
    setAnchorEl(null);
    shouldAutoFocusRef.current = false;
  }, []);

  // Open on hover
  const handleMouseEnter = useCallback((event) => {
    setAnchorEl(event.currentTarget);
    shouldAutoFocusRef.current = false;
  }, []);

  // Open on click (with focus)
  const handleClick = useCallback((event) => {
    setAnchorEl(event.currentTarget);
    shouldAutoFocusRef.current = true;
  }, []);

  // Keyboard support
  const handleKeyDown = useCallback((event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      setAnchorEl(event.currentTarget);
      shouldAutoFocusRef.current = true;
    } else if (event.key === 'Escape' && anchorEl) {
      event.preventDefault();
      handleClose();
    }
  }, [anchorEl, handleClose]);

  useEffect(() => {
    if (!anchorEl) {
      shouldAutoFocusRef.current = false;
    }
  }, [anchorEl]);

  const open = Boolean(anchorEl);

  useEffect(() => {
    if (!open || !shouldAutoFocusRef.current) {
      return;
    }

    const focusTarget = linkRef.current;
    if (!focusTarget) {
      shouldAutoFocusRef.current = false;
      return;
    }

    const focusFn = () => {
      try {
        focusTarget.focus({ preventScroll: true });
      } catch (error) {
        // noop - focusing is best effort only
      }
      shouldAutoFocusRef.current = false;
    };

    if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(focusFn);
    } else {
      focusFn();
    }
  }, [open]);

  // If no citation data, render non-clickable citation
  if (!citationData) {
    return children;
  }

  // Extract domain from URL for favicon
  const extractDomain = (url) => {
    try {
      const urlObj = new URL(url);
      return urlObj.hostname.replace('www.', '');
    } catch (e) {
      return null;
    }
  };

  const domain = extractDomain(citationData.url);

  // Citation URLs come from research / model output. Only attach a navigable
  // anchor when the scheme is on the allowlist; otherwise the card still shows
  // the source title/domain but is not clickable (no anchor, no dangerous href).
  const citationUrlIsSafe = isSafeHref(citationData.url);

  return (
    <>
      <Box
        component="sup"
        role="button"
        tabIndex={0}
        aria-haspopup="dialog"
        aria-controls={open ? popoverId : undefined}
        aria-expanded={open ? 'true' : 'false'}
        onMouseEnter={handleMouseEnter}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        sx={{
          fontSize: theme.typography.pxToRem(10),
          color: 'inherit',
          fontWeight: theme.typography.fontWeightMedium,
          marginLeft: theme.spacing(0.25),
          marginRight: theme.spacing(0.15),
          cursor: 'pointer',
          outline: 'none',
          '&:hover, &:focus-visible': {
            opacity: 0.7
          }
        }}
      >
        [{citationNumber}]
      </Box>

      <Popover
        id={open ? popoverId : undefined}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        disableRestoreFocus={true}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        slotProps={{
          paper: {
            sx: {
              maxWidth: theme.spacing(50),
              borderRadius: getChatRadii(theme).card,
              boxShadow: theme.shadows[8],
              border: getChatBorders(theme).resting,
              pointerEvents: 'auto'
            }
          }
        }}
      >
        <Box
          component={citationUrlIsSafe ? 'a' : 'div'}
          href={citationUrlIsSafe ? citationData.url : undefined}
          target={citationUrlIsSafe ? '_blank' : undefined}
          rel={citationUrlIsSafe ? 'noopener noreferrer' : undefined}
          ref={linkRef}
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: theme.spacing(tokens.spacing.sm),
            padding: theme.spacing(tokens.spacing.md),
            textDecoration: 'none',
            color: 'inherit',
            transition: 'background-color 0.15s',
            '&:hover': {
              backgroundColor: theme.palette.action.hover
            }
          }}
        >
          {/* Favicon */}
          <Box sx={{
            width: theme.spacing(2.5),
            height: theme.spacing(2.5),
            flexShrink: 0,
            borderRadius: getChatRadii(theme).chip,
            backgroundColor: theme.palette.action.hover,
            backgroundImage: domain ? `url(https://www.google.com/s2/favicons?domain=${domain}&sz=32)` : 'none',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginTop: theme.spacing(0.25)
          }}>
            {!domain && <PublicIcon sx={{ fontSize: theme.typography.pxToRem(14), color: 'text.secondary' }} />}
          </Box>

          {/* Citation details */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: theme.typography.pxToRem(13),
                lineHeight: 1.4,
                color: theme.palette.text.primary,
                fontWeight: theme.typography.fontWeightMedium,
                marginBottom: theme.spacing(tokens.spacing.quark * 2),
                overflowWrap: 'break-word',
                wordBreak: 'break-word'
              }}
            >
              {citationData.title}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.text.secondary,
                display: 'block',
                fontSize: theme.typography.pxToRem(11),
                overflowWrap: 'break-word',
                wordBreak: 'break-word'
              }}
            >
              {domain || citationData.url}
            </Typography>
          </Box>
        </Box>
      </Popover>
    </>
  );
};

// Memoize CitationTooltip to prevent re-renders and flickering
const CitationTooltip = React.memo(CitationTooltipComponent, (prevProps, nextProps) => {
  return (
    prevProps.citationNumber === nextProps.citationNumber &&
    prevProps.citationData === nextProps.citationData &&
    prevProps.theme === nextProps.theme &&
    prevProps.tokens === nextProps.tokens
  );
});

// Href-scheme allowlist for AI/research-sourced links.
//
// Markdown links (`[label](url)`) carry an arbitrary href that originates from
// model output or external research content. Without a guard, an injected link
// like `[click me](javascript:...)` or a `data:`/`vbscript:`/`file:` URL would
// render a live anchor that executes on click. We allow ONLY http:, https:, and
// mailto:; anything else (including scheme-relative tricks that resolve to a
// dangerous scheme) is treated as unsafe and the link is rendered as plain text
// with no anchor. Fails closed: anything we cannot confidently parse is unsafe.
const SAFE_LINK_SCHEMES = new Set(['http:', 'https:', 'mailto:']);

const isSafeHref = (href) => {
  if (typeof href !== 'string') {
    return false;
  }

  const trimmed = href.trim();
  if (!trimmed) {
    return false;
  }

  // Reject control characters (incl. embedded NUL/newlines/tabs) that browsers
  // strip before scheme parsing, e.g. `java\nscript:` would otherwise slip past
  // a naive check.
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001F\u007F-\u009F]/.test(trimmed)) {
    return false;
  }

  // Scheme-relative (`//host/...`) and path/anchor-relative hrefs have no
  // scheme of their own and resolve against the current page over http(s).
  if (trimmed.startsWith('//') || trimmed.startsWith('/') ||
      trimmed.startsWith('#') || trimmed.startsWith('?')) {
    return true;
  }

  // If there is no scheme delimiter before the first `/`, `?`, or `#`, treat it
  // as a relative reference (safe). A `:` that appears only later in a path is
  // not a scheme.
  const schemeMatch = trimmed.match(/^([a-zA-Z][a-zA-Z0-9+.-]*):/);
  if (!schemeMatch) {
    const firstColon = trimmed.indexOf(':');
    const firstSlash = trimmed.indexOf('/');
    if (firstColon === -1 || (firstSlash !== -1 && firstSlash < firstColon)) {
      return true;
    }
    // A leading `:` or a malformed scheme is not something we can vouch for.
    return false;
  }

  return SAFE_LINK_SCHEMES.has(schemeMatch[1].toLowerCase() + ':');
};

// Normalize Unicode characters to prevent encoding issues
const normalizeText = (text) => {
  if (typeof text !== 'string') {
    return text;
  }

  return text
    .replace(/[\u2018\u2019]/g, "'")  // Smart quotes → '
    .replace(/[\u201C\u201D]/g, '"')  // Smart quotes → "
    .replace(/\u2026/g, '...')        // Ellipsis → ...
    .replace(/[\u2013\u2014]/g, '-')  // En/em dash → -
    .replace(/\u00A0/g, ' ')          // Non-breaking space → space
    .replace(/\u00AD/g, '')           // Soft hyphen → empty
    .replace(/\uFFFD/g, '')           // Replacement character
    .replace(/[\u0000-\u0009\u000B-\u000C\u000E-\u001F\u007F-\u009F]/g, '');
};

const EnhancedMarkdownRenderer = ({ content, theme, inline = false, citationMap = null }) => {
  const tokens = React.useMemo(() => getRestackTokens(theme), [theme]);

  if (content == null || typeof content !== 'string') {
    return null;
  }

  const normalizedContent = normalizeText(content).trim();
  if (!normalizedContent) {
    return null;
  }

  // If inline mode, just render inline formatting without block elements
  if (inline) {
    const bodyColor = theme.palette.text.primary;
    const codeBackground = alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.18 : 0.08);
    const codePaddingY = theme.spacing(tokens.spacing.quark * 3);
    const codePaddingX = theme.spacing(tokens.spacing.nano);
    const codeRadius = getChatRadii(theme).nested;
    const codeFontSize = theme.typography.pxToRem(12);

    // Helper for inline rendering (need to access these in inline mode)
    const renderInlineHelper = (text, keyPrefix) => {
      if (!text) return '';

      const parts = [];
      let remaining = text;
      let partIndex = 0;

      while (remaining) {
        const patterns = [
          { regex: /^(https?:\/\/[^\s<)]+)/, type: 'url' },
          { regex: /^(\[\d+\])/, type: 'citation' },
          { regex: /^(`[^`]+`)/, type: 'inline_code' },
          { regex: /^(\*\*[^*]+\*\*)/, type: 'bold' },
          { regex: /^(__[^_]+__)/, type: 'bold' },
          { regex: /^(\*[^*]+\*)/, type: 'italic' },
          { regex: /^(_[^_]+_)/, type: 'italic' },
        ];

        let matched = false;
        for (const pattern of patterns) {
          const match = remaining.match(pattern.regex);
          if (match) {
            const matchedText = match[1];
            remaining = remaining.slice(matchedText.length);

            if (pattern.type === 'url') {
              if (isSafeHref(matchedText)) {
                parts.push(
                  <Box
                    component="a"
                    href={matchedText}
                    target="_blank"
                    rel="noopener noreferrer"
                    key={`${keyPrefix}-${partIndex++}`}
                    sx={{
                      color: theme.palette.primary.main,
                      textDecoration: 'none',
                      borderBottom: `${getChatBorders(theme).width}px solid ${theme.palette.primary.main}`,
                      '&:hover': {
                        textDecoration: 'underline',
                        borderBottom: 'none'
                      }
                    }}
                  >
                    {matchedText}
                  </Box>
                );
              } else {
                // Defensive fail-closed: render the URL text only, no anchor.
                parts.push(
                  <React.Fragment key={`${keyPrefix}-${partIndex++}`}>
                    {matchedText}
                  </React.Fragment>
                );
              }
            } else if (pattern.type === 'citation') {
              // Extract citation number from [1] format
              const citationNumber = parseInt(matchedText.replace(/\[|\]/g, ''), 10);
              const citationData = citationMap ? citationMap.get(citationNumber) : null;

              parts.push(
                <CitationTooltip
                  key={`${keyPrefix}-${partIndex++}`}
                  citationNumber={citationNumber}
                  citationData={citationData}
                  theme={theme}
                  tokens={tokens}
                >
                  <Box
                    component="sup"
                    sx={{
                      fontSize: theme.typography.pxToRem(10),
                      color: 'inherit',
                      fontWeight: theme.typography.fontWeightMedium,
                      marginLeft: theme.spacing(0.25),
                      marginRight: theme.spacing(0.15)
                    }}
                  >
                    {matchedText}
                  </Box>
                </CitationTooltip>
              );
            } else if (pattern.type === 'inline_code') {
              parts.push(
                <Box
                  component="code"
                  key={`${keyPrefix}-${partIndex++}`}
                  sx={{
                    backgroundColor: codeBackground,
                    paddingY: codePaddingY,
                    paddingX: codePaddingX,
                    borderRadius: codeRadius,
                    fontSize: codeFontSize,
                    fontFamily: 'monospace'
                  }}
                >
                  {matchedText.slice(1, -1)}
                </Box>
              );
            } else if (pattern.type === 'bold') {
              const innerText = matchedText.slice(2, -2);
              parts.push(
                <strong key={`${keyPrefix}-${partIndex++}`}>
                  {renderInlineHelper(innerText, `${keyPrefix}-bold`)}
                </strong>
              );
            } else if (pattern.type === 'italic') {
              const innerText = matchedText.slice(1, -1);
              parts.push(
                <em key={`${keyPrefix}-${partIndex++}`}>
                  {renderInlineHelper(innerText, `${keyPrefix}-italic`)}
                </em>
              );
            }

            matched = true;
            break;
          }
        }

        if (!matched) {
          parts.push(remaining[0]);
          remaining = remaining.slice(1);
        }
      }

      return parts;
    };

    return (
      <Box
        component="span"
        sx={{
          display: 'inline',
          color: bodyColor,
          fontSize: theme.typography.pxToRem(13),
          lineHeight: 1.6
        }}
      >
        {renderInlineHelper(normalizedContent, 'inline')}
      </Box>
    );
  }

  // Theme colors
  const bodyColor = theme.palette.text.primary;
  const headerColor = theme.palette.text.primary;
  const codeBackground = alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.18 : 0.08);
  const codePaddingY = theme.spacing(tokens.spacing.quark * 3);
  const codePaddingX = theme.spacing(tokens.spacing.nano);
  const codeRadius = getChatRadii(theme).nested;
  const codeFontSize = theme.typography.pxToRem(12);

  // Parse content into blocks - be flexible with line breaks
  const parseBlocks = (text) => {
    const blocks = [];
    const lines = text.split('\n');
    let currentBlock = { type: null, content: [] };

    const finishBlock = () => {
      if (currentBlock.content.length > 0) {
        blocks.push({ ...currentBlock });
        currentBlock = { type: null, content: [] };
      }
    };

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const trimmedLine = line.trim();

      // Skip empty lines between blocks
      if (!trimmedLine) {
        finishBlock();
        continue;
      }

      // Code block
      if (trimmedLine.startsWith('```')) {
        finishBlock();
        const codeLines = [];
        i++; // Skip opening ```
        while (i < lines.length && !lines[i].trim().startsWith('```')) {
          codeLines.push(lines[i]);
          i++;
        }
        blocks.push({ type: 'code_block', content: codeLines.join('\n') });
        continue;
      }

      // Header
      const headerMatch = trimmedLine.match(/^(#{1,6})\s+(.+)$/);
      if (headerMatch) {
        finishBlock();
        blocks.push({
          type: 'header',
          level: headerMatch[1].length,
          content: headerMatch[2]
        });
        continue;
      }

      // Horizontal rule — `---`, `***`, or `___` (3+ of the same on its own
      // line). Used by the AI to separate sections in long reports.
      if (/^(-{3,}|\*{3,}|_{3,})\s*$/.test(trimmedLine)) {
        finishBlock();
        blocks.push({ type: 'hr' });
        continue;
      }

      // Blockquote — one or more consecutive lines starting with `>`.
      // Used by the AI for callouts ("Note:", "Key takeaway:", pull-quotes
      // from research sources). Inner content is collected with the leading
      // `> ` stripped and handed to renderInline so bold/links/citations
      // inside the quote still render.
      if (/^>\s?/.test(trimmedLine)) {
        finishBlock();
        const quoteLines = [];
        while (i < lines.length && /^>\s?/.test(lines[i].trim())) {
          quoteLines.push(lines[i].trim().replace(/^>\s?/, ''));
          i++;
        }
        i--; // for-loop will i++
        blocks.push({ type: 'blockquote', content: quoteLines.join(' ') });
        continue;
      }

      // GFM-style pipe table. Detection: the current line looks like a
      // table row (starts and ends with `|` and has an internal `|`) AND
      // the next non-empty line is a separator row (only `|`, `-`, `:`,
      // spaces, and contains at least one `-`). Without this branch every
      // row fell through to the paragraph case and got joined with a
      // single space, producing the `| Header | Value | |---|---| | ...`
      // run-on we saw in the chat.
      const looksLikeTableRow = (l) => {
        const t = l.trim();
        return t.startsWith('|') && t.length > 1 && t.indexOf('|', 1) > 0;
      };
      const isTableSeparator = (l) => {
        const t = l.trim();
        if (!t.includes('-')) return false;
        return /^[\s|:\-]+$/.test(t);
      };
      const parseTableRow = (l) => {
        const t = l.trim().replace(/^\|/, '').replace(/\|$/, '');
        return t.split('|').map(c => c.trim());
      };

      if (looksLikeTableRow(trimmedLine)) {
        const nextLine = lines[i + 1];
        if (nextLine !== undefined && isTableSeparator(nextLine)) {
          finishBlock();
          const headers = parseTableRow(trimmedLine);
          const rows = [];
          let j = i + 2; // past header + separator
          while (j < lines.length && looksLikeTableRow(lines[j])) {
            // Guard: if a stray separator row appears mid-table, skip it.
            if (!isTableSeparator(lines[j])) {
              rows.push(parseTableRow(lines[j]));
            }
            j++;
          }
          blocks.push({ type: 'table', headers, rows });
          i = j - 1; // for-loop will i++
          continue;
        }
      }

      // Lists — unified bullet / numbered / task-list with nesting support.
      //
      // The parser peeks ahead from the current line and consumes every
      // consecutive list line, tracking each line's leading-indent. A line
      // with strictly greater indent than the previous item becomes a
      // nested sub-list inside that item; equal indent appends at the same
      // level; lesser indent unwinds the stack.
      //
      // Task lists: a bullet followed by `[ ] ` or `[x] ` is captured as a
      // checkbox item (`checked: false | true`); regular bullets get
      // `checked: null` and render with a disc bullet.
      const countIndent = (rawLine) => {
        const lead = rawLine.match(/^[ \t]*/)[0];
        let n = 0;
        for (const ch of lead) n += (ch === '\t') ? 4 : 1;
        return n;
      };
      const parseListLine = (rawLine) => {
        const indent = countIndent(rawLine);
        const rest = rawLine.trimStart();
        const bullet = rest.match(/^([-*•])\s+(\[([ xX])\]\s+)?(.+)$/);
        if (bullet) {
          return {
            indent,
            ordered: false,
            checked: bullet[3] == null ? null : /[xX]/.test(bullet[3]),
            text: bullet[4]
          };
        }
        const numbered = rest.match(/^(\d+)\.\s+(.+)$/);
        if (numbered) {
          return { indent, ordered: true, checked: null, text: numbered[2] };
        }
        return null;
      };

      const firstItem = parseListLine(line);
      if (firstItem) {
        finishBlock();
        const rootList = { type: 'list', ordered: firstItem.ordered, items: [] };
        rootList.items.push({
          text: firstItem.text,
          checked: firstItem.checked,
          subList: null
        });
        const stack = [{ indent: firstItem.indent, list: rootList }];

        let j = i + 1;
        while (j < lines.length) {
          const nextRaw = lines[j];
          if (!nextRaw.trim()) break;           // blank terminates the list
          const item = parseListLine(nextRaw);
          if (!item) break;                     // non-list line terminates

          // Unwind the stack to the level matching this item's indent.
          while (stack.length > 1 && item.indent < stack[stack.length - 1].indent) {
            stack.pop();
          }

          const top = stack[stack.length - 1];
          if (item.indent > top.indent) {
            // Nested: attach a new sublist under the last item of `top`.
            const parentItems = top.list.items;
            const parent = parentItems[parentItems.length - 1];
            const sub = { type: 'list', ordered: item.ordered, items: [] };
            parent.subList = sub;
            sub.items.push({ text: item.text, checked: item.checked, subList: null });
            stack.push({ indent: item.indent, list: sub });
          } else {
            // Same level. If the marker family switched (bullet ↔ numbered),
            // terminate this list; the outer loop will start a fresh block.
            if (item.ordered !== top.list.ordered) break;
            top.list.items.push({
              text: item.text,
              checked: item.checked,
              subList: null
            });
          }

          j++;
        }

        blocks.push(rootList);
        i = j - 1;   // for-loop will i++
        continue;
      }

      // Regular paragraph
      if (currentBlock.type !== 'paragraph') {
        finishBlock();
        currentBlock = { type: 'paragraph', content: [] };
      }
      currentBlock.content.push(line);
    }

    finishBlock();
    return blocks;
  };

  // Render inline formatting (bold, italic, code, citations)
  const renderInline = (text, keyPrefix) => {
    if (!text) return '';

    const parts = [];
    let remaining = text;
    let partIndex = 0;

    while (remaining) {
      // Try to match patterns in order of priority.
      // NOTE: the markdown-link pattern `[label](url)` MUST come before the
      // citation pattern — otherwise `[1](https://...)` (a digit-labelled
      // clickable citation) would be truncated to just `[1]` and the URL
      // would leak out as plain text.
      const patterns = [
        { regex: /^(https?:\/\/[^\s<)]+)/, type: 'url' },  // bare URLs
        { regex: /^\[([^\]]+)\]\(([^)]+)\)/, type: 'link' },
        { regex: /^(\[\d+\])/, type: 'citation' },
        { regex: /^(`[^`]+`)/, type: 'inline_code' },
        { regex: /^(~~[^~]+~~)/, type: 'strike' },
        { regex: /^(\*\*[^*]+\*\*)/, type: 'bold' },
        { regex: /^(__[^_]+__)/, type: 'bold' },
        { regex: /^(\*[^*]+\*)/, type: 'italic' },
        { regex: /^(_[^_]+_)/, type: 'italic' },
      ];

      let matched = false;
      for (const pattern of patterns) {
        const match = remaining.match(pattern.regex);
        if (match) {
          // `link` consumes `match[0]` (the whole `[label](url)`), every other
          // pattern consumes `match[1]`.
          const matchedText = pattern.type === 'link' ? match[0] : match[1];
          remaining = remaining.slice(matchedText.length);

          if (pattern.type === 'url') {
            if (isSafeHref(matchedText)) {
              parts.push(
                <Box
                  component="a"
                  href={matchedText}
                  target="_blank"
                  rel="noopener noreferrer"
                  key={`${keyPrefix}-${partIndex++}`}
                  sx={{
                    color: theme.palette.primary.main,
                    textDecoration: 'none',
                    borderBottom: `${getChatBorders(theme).width}px solid ${theme.palette.primary.main}`,
                    '&:hover': {
                      textDecoration: 'underline',
                      borderBottom: 'none'
                    }
                  }}
                >
                  {matchedText}
                </Box>
              );
            } else {
              // Defensive fail-closed: render the URL text only, no anchor.
              parts.push(
                <React.Fragment key={`${keyPrefix}-${partIndex++}`}>
                  {matchedText}
                </React.Fragment>
              );
            }
          } else if (pattern.type === 'link') {
            const label = match[1];
            const url = match[2];
            if (isSafeHref(url)) {
              parts.push(
                <Box
                  component="a"
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  key={`${keyPrefix}-${partIndex++}`}
                  sx={{
                    color: theme.palette.primary.main,
                    textDecoration: 'none',
                    borderBottom: `${getChatBorders(theme).width}px solid ${theme.palette.primary.main}`,
                    '&:hover': {
                      textDecoration: 'underline',
                      borderBottom: 'none'
                    }
                  }}
                >
                  {renderInline(label, `${keyPrefix}-link-${partIndex}`)}
                </Box>
              );
            } else {
              // Unsafe scheme (javascript:, data:, vbscript:, file:, ...). Drop
              // the anchor and render the link label as plain text so injected
              // or AI-sourced dangerous links cannot execute.
              parts.push(
                <React.Fragment key={`${keyPrefix}-${partIndex++}`}>
                  {renderInline(label, `${keyPrefix}-link-unsafe-${partIndex}`)}
                </React.Fragment>
              );
            }
          } else if (pattern.type === 'citation') {
            // Extract citation number from [1] format
            const citationNumber = parseInt(matchedText.replace(/\[|\]/g, ''), 10);
            const citationData = citationMap ? citationMap.get(citationNumber) : null;

            parts.push(
              <CitationTooltip
                key={`${keyPrefix}-${partIndex++}`}
                citationNumber={citationNumber}
                citationData={citationData}
                theme={theme}
                tokens={tokens}
              >
                <Box
                  component="sup"
                  sx={{
                    fontSize: theme.typography.pxToRem(10),
                    color: 'inherit',
                    fontWeight: theme.typography.fontWeightMedium,
                    marginLeft: theme.spacing(0.25),
                    marginRight: theme.spacing(0.15)
                  }}
                >
                  {matchedText}
                </Box>
              </CitationTooltip>
            );
          } else if (pattern.type === 'inline_code') {
            parts.push(
              <Box
                component="code"
                key={`${keyPrefix}-${partIndex++}`}
                sx={{
                  backgroundColor: codeBackground,
                  paddingY: codePaddingY,
                  paddingX: codePaddingX,
                  borderRadius: codeRadius,
                  fontSize: codeFontSize,
                  fontFamily: 'monospace'
                }}
              >
                {matchedText.slice(1, -1)}
              </Box>
            );
          } else if (pattern.type === 'strike') {
            const innerText = matchedText.slice(2, -2);
            parts.push(
              <Box
                component="s"
                key={`${keyPrefix}-${partIndex++}`}
                sx={{ textDecoration: 'line-through', opacity: 0.75 }}
              >
                {renderInline(innerText, `${keyPrefix}-strike`)}
              </Box>
            );
          } else if (pattern.type === 'bold') {
            const innerText = matchedText.slice(2, -2);
            parts.push(
              <strong key={`${keyPrefix}-${partIndex++}`}>
                {renderInline(innerText, `${keyPrefix}-bold`)}
              </strong>
            );
          } else if (pattern.type === 'italic') {
            const innerText = matchedText.slice(1, -1);
            parts.push(
              <em key={`${keyPrefix}-${partIndex++}`}>
                {renderInline(innerText, `${keyPrefix}-italic`)}
              </em>
            );
          }

          matched = true;
          break;
        }
      }

      if (!matched) {
        // Take one character as plain text
        parts.push(remaining[0]);
        remaining = remaining.slice(1);
      }
    }

    return parts;
  };

  // Recursive list renderer — handles bullet, numbered, and task-list items,
  // plus unlimited nesting depth. Called once per top-level list block; recurses
  // into `item.subList` for nested sublists.
  const renderList = (listBlock, keyPrefix, depth = 0) => {
    const accent = theme.palette.primary.main;
    const Component = listBlock.ordered ? 'ol' : 'ul';
    const hasAnyTask = listBlock.items.some(it => it.checked !== null);

    return (
      <Box
        key={keyPrefix}
        component={Component}
        sx={{
          margin: 0,
          // Task-list rows replace the disc with a custom checkbox, so we
          // strip the marker padding and list-style for the whole container.
          paddingLeft: hasAnyTask ? 0 : theme.spacing(2.5),
          marginBottom: depth === 0 ? theme.spacing(tokens.spacing.md) : 0,
          marginTop: depth === 0 ? 0 : theme.spacing(tokens.spacing.xs),
          listStyle: hasAnyTask ? 'none' : (listBlock.ordered ? 'decimal' : 'disc'),
          listStylePosition: 'outside',
        }}
      >
        {listBlock.items.map((item, idx) => {
          const itemKey = `${keyPrefix}-item-${idx}`;
          const isTask = item.checked !== null;
          return (
            <Box
              key={itemKey}
              component="li"
              sx={{
                fontSize: theme.typography.pxToRem(13),
                lineHeight: 1.6,
                color: bodyColor,
                fontWeight: theme.typography.fontWeightRegular,
                marginBottom: theme.spacing(tokens.spacing.sm),
                overflowWrap: 'break-word',
                wordBreak: 'break-word',
                display: isTask ? 'flex' : 'list-item',
                alignItems: isTask ? 'flex-start' : undefined,
                gap: isTask ? theme.spacing(tokens.spacing.xs) : undefined,
                '&:last-child': { marginBottom: 0 }
              }}
            >
              {isTask && (
                <Box
                  component="span"
                  aria-hidden
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    width: '14px',
                    height: '14px',
                    marginTop: '3px',
                    // Deliberate exception to the radius scale, shared with the
                    // UserQuestionCard multi-select box: at 14px the 8px chip
                    // radius rounds a checkbox into a circle and it reads as a
                    // radio. 4px is the one off-scale radius in the chat pane.
                    borderRadius: '4px',
                    border: `${getChatBorders(theme).emphasisWidth}px solid ${alpha(accent, item.checked ? 0.9 : 0.5)}`,
                    backgroundColor: item.checked
                      ? alpha(accent, theme.palette.mode === 'dark' ? 0.35 : 0.25)
                      : 'transparent',
                    color: accent,
                    fontSize: '11px',
                    lineHeight: 1,
                    fontWeight: 700
                  }}
                >
                  {item.checked ? '✓' : ''}
                </Box>
              )}
              <Box
                component="span"
                sx={{
                  flex: isTask ? 1 : undefined,
                  textDecoration: item.checked ? 'line-through' : 'none',
                  opacity: item.checked ? 0.7 : 1
                }}
              >
                {renderInline(item.text, itemKey)}
              </Box>
              {item.subList && renderList(item.subList, `${itemKey}-sub`, depth + 1)}
            </Box>
          );
        })}
      </Box>
    );
  };

  // Parse and render blocks
  const blocks = parseBlocks(normalizedContent);
  const elements = [];

  blocks.forEach((block, blockIndex) => {
    const key = `block-${blockIndex}`;

    if (block.type === 'header') {
      const variant = block.level === 1 ? 'h6' : block.level === 2 ? 'subtitle1' : 'subtitle2';
      elements.push(
        <Typography
          key={key}
          variant={variant}
          sx={{
            color: headerColor,
            fontWeight: theme.typography.fontWeightMedium,
            lineHeight: 1.4,
            marginBottom: theme.spacing(tokens.spacing.sm),
            marginTop: blockIndex > 0 ? theme.spacing(tokens.spacing.md) : 0,
            overflowWrap: 'break-word',
            wordBreak: 'break-word'
          }}
        >
          {renderInline(block.content, key)}
        </Typography>
      );
    } else if (block.type === 'list') {
      elements.push(renderList(block, key));
    } else if (block.type === 'table') {
      elements.push(
        <Box
          key={key}
          sx={{
            marginBottom: theme.spacing(tokens.spacing.md),
            overflowX: 'auto',
            maxWidth: '100%',
            borderRadius: getChatRadii(theme).nested,
            border: getChatBorders(theme).resting
          }}
        >
          <Box
            component="table"
            sx={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: theme.typography.pxToRem(13),
              color: bodyColor,
              tableLayout: 'auto',
              '& th, & td': {
                padding: `${theme.spacing(tokens.spacing.sm)} ${theme.spacing(tokens.spacing.md)}`,
                textAlign: 'left',
                verticalAlign: 'top',
                borderBottom: `${getChatBorders(theme).width}px solid ${theme.palette.divider}`,
                lineHeight: 1.5,
                overflowWrap: 'break-word',
                wordBreak: 'normal'
              },
              '& th': {
                backgroundColor: alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.06 : 0.04),
                fontWeight: theme.typography.fontWeightMedium,
                color: theme.palette.text.primary,
                fontSize: theme.typography.pxToRem(11),
                textTransform: 'uppercase',
                letterSpacing: '0.04em'
              },
              '& tbody tr:last-of-type td': {
                borderBottom: 'none'
              },
              '& tbody tr:nth-of-type(even) td': {
                backgroundColor: alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.02 : 0.015)
              }
            }}
          >
            {block.headers && block.headers.length > 0 && (
              <thead>
                <tr>
                  {block.headers.map((h, idx) => (
                    <th key={`${key}-h-${idx}`}>{renderInline(h, `${key}-h-${idx}`)}</th>
                  ))}
                </tr>
              </thead>
            )}
            <tbody>
              {block.rows.map((row, rIdx) => (
                <tr key={`${key}-r-${rIdx}`}>
                  {row.map((cell, cIdx) => (
                    <td key={`${key}-r-${rIdx}-c-${cIdx}`}>
                      {renderInline(cell, `${key}-r-${rIdx}-c-${cIdx}`)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </Box>
        </Box>
      );
    } else if (block.type === 'code_block') {
      elements.push(
        <Box
          key={key}
          component="pre"
          sx={{
            backgroundColor: codeBackground,
            padding: theme.spacing(tokens.spacing.sm),
            borderRadius: codeRadius,
            fontSize: codeFontSize,
            fontFamily: 'monospace',
            overflowX: 'auto',
            maxWidth: '100%',
            marginBottom: theme.spacing(tokens.spacing.md),
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            '& code': {
              fontFamily: 'inherit',
              fontSize: 'inherit',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word'
            }
          }}
        >
          <code>{block.content}</code>
        </Box>
      );
    } else if (block.type === 'hr') {
      elements.push(
        <Divider
          key={key}
          sx={{
            marginTop: theme.spacing(tokens.spacing.md),
            marginBottom: theme.spacing(tokens.spacing.md),
            borderColor: theme.palette.divider
          }}
        />
      );
    } else if (block.type === 'blockquote') {
      const accent = theme.palette.primary.main;
      elements.push(
        <Box
          key={key}
          component="blockquote"
          sx={{
            margin: 0,
            marginBottom: theme.spacing(tokens.spacing.md),
            paddingTop: theme.spacing(tokens.spacing.xs),
            paddingBottom: theme.spacing(tokens.spacing.xs),
            paddingLeft: theme.spacing(tokens.spacing.sm),
            paddingRight: theme.spacing(tokens.spacing.sm),
            borderLeft: `${getChatBorders(theme).emphasisWidth}px solid ${alpha(accent, 0.55)}`,
            backgroundColor: alpha(
              accent,
              theme.palette.mode === 'dark' ? 0.08 : 0.05
            ),
            borderRadius: getChatRadii(theme).nested,
            color: bodyColor,
            fontStyle: 'italic',
            overflowWrap: 'break-word',
            wordBreak: 'break-word'
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: 'inherit',
              fontStyle: 'inherit',
              lineHeight: 1.58,
              margin: 0
            }}
          >
            {renderInline(block.content, key)}
          </Typography>
        </Box>
      );
    } else if (block.type === 'paragraph') {
      const paragraphText = block.content.join(' ').trim();
      if (paragraphText) {
        elements.push(
          <Typography
            key={key}
            variant="body2"
            sx={{
              color: bodyColor,
              lineHeight: 1.58,
              marginBottom: theme.spacing(tokens.spacing.md),
              overflowWrap: 'break-word',
              wordBreak: 'normal'
            }}
          >
            {renderInline(paragraphText, key)}
          </Typography>
        );
      }
    }
  });

  return (
    <Box
      sx={{
        '& > *:last-child': { marginBottom: 0 },
        maxWidth: '100%',
        overflowWrap: 'break-word',
        wordBreak: 'normal'
      }}
    >
      {elements}
    </Box>
  );
};

export default EnhancedMarkdownRenderer;
